/**
 * ATS Report V3 - Score Helper Utilities
 *
 * Pure functions for score variant classification, label generation,
 * category icon mapping, and severity coloring.
 */

// ---------------------------------------------------------------------------
// Score variant
// ---------------------------------------------------------------------------

export type ScoreVariant = 'excellent' | 'good' | 'fair' | 'low';

/**
 * Map a 0-100 score to a color variant.
 *
 * - >= 80: excellent (green)
 * - >= 60: good (amber)
 * - >= 40: fair (orange)
 * - < 40:  low (red)
 */
export function getScoreVariant(score: number): ScoreVariant {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'low';
}

/**
 * Map a 1-10 subsection score to a color variant.
 * Uses the same thresholds scaled to 10-point range.
 */
export function getSubScoreVariant(score: number): ScoreVariant {
  if (score >= 8) return 'excellent';
  if (score >= 6) return 'good';
  if (score >= 4) return 'fair';
  return 'low';
}

// ---------------------------------------------------------------------------
// Score label
// ---------------------------------------------------------------------------

/**
 * Human-readable label for a 0-100 overall score.
 */
export function getScoreLabel(score: number): string {
  if (score >= 90) return 'ATS-Ready';
  if (score >= 75) return 'Almost There';
  if (score >= 60) return 'Needs Work';
  if (score >= 40) return 'At Risk';
  return 'ATS-Blocked';
}

// ---------------------------------------------------------------------------
// Category icon names (lucide-react icon component names)
// ---------------------------------------------------------------------------

export type CategoryKey = 'completeness' | 'roleAlignment' | 'impact';

/**
 * Returns a lucide-react icon name for a scoring category.
 */
export function getCategoryIcon(category: CategoryKey): string {
  switch (category) {
    case 'completeness':
      return 'Target';
    case 'roleAlignment':
      return 'Trophy';
    case 'impact':
      return 'Zap';
  }
}

// ---------------------------------------------------------------------------
// Severity / Priority CSS variable references
// ---------------------------------------------------------------------------

/**
 * Returns a CSS variable reference for a finding severity.
 */
export function getSeverityColor(severity: 'critical' | 'warning' | 'info'): string {
  switch (severity) {
    case 'critical':
      return 'var(--ats-severity-critical)';
    case 'warning':
      return 'var(--ats-severity-warning)';
    case 'info':
      return 'var(--ats-severity-info)';
  }
}

/**
 * Returns a CSS variable reference for an improvement priority.
 */
export function getPriorityColor(priority: 'high' | 'medium' | 'low'): string {
  switch (priority) {
    case 'high':
      return 'var(--ats-priority-high)';
    case 'medium':
      return 'var(--ats-priority-medium)';
    case 'low':
      return 'var(--ats-priority-low)';
  }
}

// ---------------------------------------------------------------------------
// Class name helper (lightweight cn replacement)
// ---------------------------------------------------------------------------

type ClassValue = string | number | boolean | undefined | null;

/**
 * Lightweight class name merger. Filters out falsy values and joins with space.
 * Replaces the need for clsx/classnames/cn from external packages.
 */
export function cx(...classes: ClassValue[]): string {
  return classes
    .filter((cls): cls is string => typeof cls === 'string' && cls.length > 0)
    .join(' ');
}
